import confetti from "canvas-confetti";
import { useState } from "react";
import type { Player } from "../types";

interface MatchRecorderProps {
	players: Player[];
	onRecordMatch: (winnerId: string, loserId: string) => void;
	onUndo: () => void;
	canUndo: boolean;
}

export function MatchRecorder({
	players,
	onRecordMatch,
	onUndo,
	canUndo,
}: MatchRecorderProps) {
	const [winnerId, setWinnerId] = useState("");
	const [loserId, setLoserId] = useState("");
	const [lastWinner, setLastWinner] = useState<string | null>(null);
	const [lastMatchPair, setLastMatchPair] = useState<{
		winnerId: string;
		loserId: string;
	} | null>(null);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (winnerId && loserId && winnerId !== loserId) {
			const winner = players.find((p) => p.id === winnerId);

			// Save the match pair for rematch
			setLastMatchPair({ winnerId, loserId });

			onRecordMatch(winnerId, loserId);

			// Show confetti and congratulations
			if (winner) {
				setLastWinner(winner.name);
				confetti({
					particleCount: 100,
					spread: 70,
					origin: { y: 0.6 },
				});

				// Hide message after 3 seconds
				setTimeout(() => setLastWinner(null), 3000);
			}

			setWinnerId("");
			setLoserId("");
		}
	};

	const handleSwap = () => {
		setWinnerId(loserId);
		setLoserId(winnerId);
	};

	const handleRematch = () => {
		if (lastMatchPair) {
			setWinnerId(lastMatchPair.winnerId);
			setLoserId(lastMatchPair.loserId);
		}
	};

	const sortedPlayers = [...players].sort((a, b) =>
		a.name.localeCompare(b.name),
	);

	// Check if last match players still exist
	const canRematch =
		lastMatchPair &&
		players.some((p) => p.id === lastMatchPair.winnerId) &&
		players.some((p) => p.id === lastMatchPair.loserId);

	if (players.length < 2) {
		return (
			<div className="card match-recorder">
				<h2>Record Match</h2>
				<p className="hint">Add at least 2 players to record matches.</p>
			</div>
		);
	}

	return (
		<div className="card match-recorder">
			<h2>Record Match</h2>
			{lastWinner && (
				<div className="winner-announcement">
					🎉 Congratulations, {lastWinner}! 🎉
				</div>
			)}
			<form onSubmit={handleSubmit}>
				<div className="match-selectors">
					<div className="selector">
						<label htmlFor="winner">Winner</label>
						<select
							id="winner"
							value={winnerId}
							onChange={(e) => setWinnerId(e.target.value)}
						>
							<option value="">Select winner</option>
							{sortedPlayers.map((player) => (
								<option
									key={player.id}
									value={player.id}
									disabled={player.id === loserId}
								>
									{player.name} ({player.elo})
								</option>
							))}
						</select>
					</div>
					<button
						type="button"
						className="btn-swap"
						onClick={handleSwap}
						disabled={!winnerId && !loserId}
						title="Swap winner and loser"
					>
						⇄
					</button>
					<div className="selector">
						<label htmlFor="loser">Loser</label>
						<select
							id="loser"
							value={loserId}
							onChange={(e) => setLoserId(e.target.value)}
						>
							<option value="">Select loser</option>
							{sortedPlayers.map((player) => (
								<option
									key={player.id}
									value={player.id}
									disabled={player.id === winnerId}
								>
									{player.name} ({player.elo})
								</option>
							))}
						</select>
					</div>
				</div>
				<div className="match-actions">
					{canUndo && (
						<button
							type="button"
							className="btn-undo"
							onClick={() => {
								if (confirm("Undo the last match?")) {
									onUndo();
								}
							}}
						>
							Undo Last
						</button>
					)}
					<button
						type="button"
						className="btn-rematch"
						onClick={handleRematch}
						disabled={!canRematch}
					>
						Rematch
					</button>
					<button
						type="submit"
						className="btn-primary"
						disabled={!winnerId || !loserId || winnerId === loserId}
					>
						Record Match
					</button>
				</div>
			</form>
		</div>
	);
}
